import {
  collection,
  doc,
  getDoc,
  getDocs,
  getDocFromCache,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  type Unsubscribe,
} from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { defensePlanPartiesApi } from './defensePlanParties';
import { DefensePlan, DefensePlanFeature, PlanBounds, PlanBundle } from '../types';
import { mapDoc, mapDocs } from '@utils/firestore';
import { AppError } from '../utils/errorHandler';
import { DefensePlanSchema } from '../schemas/firestoreSchemas';
import { planBounds } from '../utils/planGeometry';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:defensePlans');

// Firestore rejects undefined values (e.g. a feature's optional `note`).
// Strip them before every write, same as postsApi.createPost.
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

function stripFeature(feature: DefensePlanFeature): DefensePlanFeature {
  return stripUndefined(
    feature as unknown as Record<string, unknown>
  ) as unknown as DefensePlanFeature;
}

// Not exported: only defensePlansApi.createPlan uses this shape, and the
// store calls it with an inline object literal rather than importing the
// type.
interface CreatePlanData {
  ownerId: string;
  ownerName: string;
  areaId: string;
  title: string;
  features: DefensePlanFeature[];
  // Both optional, used only by the store's duplicateActivePlan: a copy
  // keeps the source plan's visibility instead of always starting 'shared',
  // and carries over the source plan's bundles.
  visibility?: DefensePlan['visibility'];
  bundles?: PlanBundle[];
}

interface WriteFeaturesData {
  features: DefensePlanFeature[];
  featureCount: number;
  bounds: PlanBounds | null;
  // Written alongside features on every call (not left optional) so the
  // two can never drift — a caller with no bundle change still passes the
  // plan's current bundles back through.
  bundles: PlanBundle[];
}

export const defensePlansApi = {
  // Pre-generates a Firestore doc id, the same shape as postsApi.createPost's
  // optional postId — lets the store build the optimistic plan object before
  // the write lands, and makes createPlan idempotent on retry.
  newPlanId(): string {
    return doc(collection(db, COLLECTIONS.DEFENSE_PLANS)).id;
  },

  // Idempotent on planId, mirroring postsApi.createPost's existence guard: a
  // retried call (e.g. a flaky first save) finds the doc already there and
  // skips straight to the re-fetch instead of clobbering it. Visibility
  // defaults to 'private' (Sept 17 2026 — was 'shared' per the Sept 9
  // decision); the owner opts into sharing later via setVisibility.
  // featureCount/bounds are computed here, not trusted from the caller, so
  // the stored doc is always self-consistent.
  async createPlan(planId: string, data: CreatePlanData): Promise<DefensePlan> {
    const docRef = doc(db, COLLECTIONS.DEFENSE_PLANS, planId);
    // A missing plan reads back as permission-denied, not as an empty
    // snapshot: the read rule evaluates resource.data, and there is no
    // resource yet. Same trap postsApi.createPost documents. Treat denied
    // as "does not exist"; the create rule still refuses a spoofed owner.
    let alreadyExists = false;
    try {
      alreadyExists = (await getDoc(docRef)).exists();
    } catch (err) {
      if ((err as { code?: string })?.code !== 'permission-denied') throw err;
    }
    if (!alreadyExists) {
      const features = data.features.map(stripFeature);
      const rawPlanData = {
        ownerId: data.ownerId,
        ownerName: data.ownerName,
        areaId: data.areaId,
        title: data.title,
        visibility: data.visibility ?? ('private' as const),
        features,
        featureCount: features.length,
        bounds: planBounds(features),
        ...(data.bundles !== undefined ? { bundles: data.bundles } : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await setDoc(docRef, stripUndefined(rawPlanData));
    }

    // Cache-first re-fetch, same reasoning as postsApi.createPost: our own
    // commit is always in the local cache, and mapDoc resolves pending
    // serverTimestamp() fields to a local estimate, so this returns
    // immediately with a schema-valid Timestamp instead of round-tripping
    // to the server.
    let snapshot;
    try {
      snapshot = await getDocFromCache(docRef);
    } catch {
      snapshot = await getDoc(docRef);
    }
    const created = mapDoc(snapshot, DefensePlanSchema);
    if (!created) {
      throw new AppError('Plan was created but could not be re-read.', 'FIRESTORE_ERROR');
    }
    return created;
  },

  // Full-array updateDoc on every commit (add or remove a feature) — bounds
  // and featureCount are cheap to recompute and this keeps the doc
  // self-consistent without a second round trip. The store serialises these
  // through a module-level promise chain so two rapid edits don't race and
  // overwrite each other's `features` array.
  async writeFeatures(planId: string, data: WriteFeaturesData): Promise<void> {
    const docRef = doc(db, COLLECTIONS.DEFENSE_PLANS, planId);
    await updateDoc(docRef, {
      features: data.features.map(stripFeature),
      featureCount: data.featureCount,
      bounds: data.bounds,
      bundles: data.bundles.map((b) => stripUndefined(b as unknown as Record<string, unknown>)),
      updatedAt: serverTimestamp(),
    });
  },

  async updateTitle(planId: string, title: string): Promise<void> {
    const docRef = doc(db, COLLECTIONS.DEFENSE_PLANS, planId);
    await updateDoc(docRef, { title, updatedAt: serverTimestamp() });
  },

  async setVisibility(planId: string, visibility: DefensePlan['visibility']): Promise<void> {
    const docRef = doc(db, COLLECTIONS.DEFENSE_PLANS, planId);
    await updateDoc(docRef, { visibility, updatedAt: serverTimestamp() });
  },

  // Deletes the plan doc AND everything under it that the rules can't
  // cascade server-side: every party (which can hold a private email in its
  // partyContacts sibling doc — see defensePlanPartiesApi.deleteParty's own
  // comment) and every assignment. All in one batch so the delete is
  // all-or-nothing — see queueDeleteAllForPlan for why the plan doc can
  // still be deleted alongside the subcollection deletes its own rules
  // check against.
  async deletePlan(planId: string): Promise<void> {
    const batch = writeBatch(db);
    await defensePlanPartiesApi.queueDeleteAllForPlan(batch, planId);
    batch.delete(doc(db, COLLECTIONS.DEFENSE_PLANS, planId));
    await batch.commit();
  },

  async getPlan(planId: string): Promise<DefensePlan | null> {
    const snapshot = await getDoc(doc(db, COLLECTIONS.DEFENSE_PLANS, planId));
    return mapDoc(snapshot, DefensePlanSchema);
  },

  // Every plan the signed-in user owns, sorted newest-first by updatedAt.
  // It used to be keyed by the viewport's area too, but a new plan takes
  // its area from its first vertex, so panning the map centre out of the
  // area, or drawing near its edge, made the editor lose an existing plan
  // and start a second one. An equality filter with no orderBy runs on
  // Firestore's automatic single-field index — a user has a handful of
  // plans at most, so sorting happens here rather than through a
  // composite-indexed query. The store picks one of these as the "active"
  // plan (explicit choice, else localStorage, else newest); this function
  // just delivers the raw list.
  subscribeOwnPlans(
    ownerId: string,
    onNext: (plans: DefensePlan[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(collection(db, COLLECTIONS.DEFENSE_PLANS), where('ownerId', '==', ownerId));
    return onSnapshotWithRetry(
      q,
      (snap) => {
        const plans = mapDocs(snap, DefensePlanSchema);
        onNext([...plans].sort((a, b) => b.updatedAt.toMillis() - a.updatedAt.toMillis()));
      },
      (error) => {
        log.error('subscribeOwnPlans failed:', error);
        onError?.(error);
      }
    );
  },

  // World-readable list of shared plans in an area, for "Plans in this area"
  // (MapLayersSheet) and the area-plans overlay. Relies on the composite
  // index (areaId ASC, visibility ASC, updatedAt DESC). No isAuthed() gate
  // on the shared read branch, so this also runs signed out.
  subscribeAreaPlans(
    areaId: string,
    onNext: (plans: DefensePlan[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(
      collection(db, COLLECTIONS.DEFENSE_PLANS),
      where('areaId', '==', areaId),
      where('visibility', '==', 'shared'),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );
    return onSnapshotWithRetry(
      q,
      (snap) => onNext(mapDocs(snap, DefensePlanSchema)),
      (error) => {
        log.error('subscribeAreaPlans failed:', error);
        onError?.(error);
      }
    );
  },

  // One-shot version of subscribeAreaPlans, same query (areaId + shared
  // visibility, most-recently-updated 50), for a reader that only needs a
  // point-in-time answer — the property page's plan suggestions don't need
  // to stay live for a plan someone else might be drawing right now.
  async getAreaPlans(areaId: string): Promise<DefensePlan[]> {
    const q = query(
      collection(db, COLLECTIONS.DEFENSE_PLANS),
      where('areaId', '==', areaId),
      where('visibility', '==', 'shared'),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );
    const snap = await getDocs(q);
    return mapDocs(snap, DefensePlanSchema);
  },

  // Single-doc subscription for whatever plan the map is currently viewing
  // (own plan being edited, or another owner's plan opened from the area
  // list or a /plan/<id> link). No index needed — a doc-id get.
  subscribePlan(
    planId: string,
    onNext: (plan: DefensePlan | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const docRef = doc(db, COLLECTIONS.DEFENSE_PLANS, planId);
    return onSnapshotWithRetry(
      docRef,
      (snap) => onNext(mapDoc(snap, DefensePlanSchema)),
      (error) => {
        // A plan link to a private or deleted plan is denied by design and
        // the viewer already says the plan isn't available; warn, don't
        // error (the dev server turns console.error into a red toast).
        if ((error as { code?: string }).code === 'permission-denied') {
          log.warn('subscribePlan denied:', error);
        } else {
          log.error('subscribePlan failed:', error);
        }
        onError?.(error);
      }
    );
  },
};
